import { useCallback, useEffect, useState } from 'react';
import { type URLSearchParamsInit, useSearchParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';

export interface FilterChangeProps<T> {
  filter: keyof T;
  value: string | boolean | number | Array<string> | Date | undefined;
}

/*
Use this hook to get the filters from the URL and update the URL when the filters change. 
This hook removes undefined values from the URL and adds default values to the URL if they are not present.
 */
export const useFilters = <T extends object>(defaultFilters?: T) => {
  const [urlFilters, setUrlFilters] = useSearchParams();

  const [formFilters, setFormFilters] = useState<T>({
    ...defaultFilters,
    ...queryString.parse(document.location.search, {
      parseNumbers: true,
      parseBooleans: true,
      sort: false,
    }),
  } as T);

  const resetFilters = useCallback(
    () => setFormFilters(defaultFilters as T),
    []
  );

  const resetPaging = () => {
    onPageChange(1);
  };

  const clearFilter = useCallback(
    (filter: keyof T) =>
      setFormFilters((prevState) => ({
        ...prevState,
        [filter]: undefined,
      })),
    []
  );

  const handleFilterChange = useCallback(
    ({ filter, value }: FilterChangeProps<T>) => {
      setFormFilters((prevState) => {
        if (prevState[filter] === value) return prevState;
        const newState: any = { ...prevState };
        newState[filter] = value === 'all' ? undefined : value;
        return newState;
      });
      return true;
    },
    [formFilters]
  );

  const handleFiltersChange = useCallback(
    (changes: FilterChangeProps<T>[]) => {
      setFormFilters((prevState) => {
        const newState: any = { ...prevState };

        changes.forEach(({ filter, value }) => {
          newState[filter] = value === 'all' ? undefined : value;
        });
        return isEqual(prevState, newState) ? prevState : newState;
      });
      return true;
    },
    [formFilters]
  );

  const onPageChange = useCallback((current: number, size?: number) => {
    setFormFilters((prevFormFilters: any) => {
      const resultsPerPage = size ?? prevFormFilters.resultsPerPage;
      // return to page 1 if page size changed
      const pageNumber =
        resultsPerPage === prevFormFilters.resultsPerPage ? current : 1;
      return {
        ...prevFormFilters,
        pageNumber,
        resultsPerPage,
      };
    });
  }, []);

  const onSizeChange = useCallback((resultsPerPage: number) => {
    setFormFilters((prevFormFilters: T) => {
      return {
        ...prevFormFilters,
        pageNumber: 1,
        resultsPerPage,
      };
    });
  }, []);

  useEffect(() => {
    if (formFilters) {
      const filtersWithoutEmptyStrings = Object.entries(formFilters).filter(
        ([, value]) => {
          return value !== undefined && value !== null && value !== '';
        }
      );

      const newUrlFilters = Object.fromEntries(
        filtersWithoutEmptyStrings
      ) as URLSearchParamsInit;

      const oldUrlFilters = queryString.parse(urlFilters.toString(), {
        parseNumbers: true,
        parseBooleans: true,
        sort: false,
      }) as T;

      // prevent circular loop
      if (!isEqual(oldUrlFilters, newUrlFilters)) setUrlFilters(newUrlFilters);
    }
  }, [formFilters]);

  return {
    setFormFilters,
    handleFilterChange,
    handleFiltersChange,
    resetFilters,
    resetPaging,
    clearFilter,
    formFilters,
    urlFilters,
    setUrlFilters,
    onPageChange,
    onSizeChange,
  };
};
